import type { ChatbotApi } from '@liks/backend-svc-api/esm/apis/ChatbotApi';
import type { Chatbot } from '@liks/backend-svc-api/esm/models/index';
import {
  DEFAULT_CHATBOT_PRIMARY_COLOR,
  type AccessibleChatbotWithIdentity,
  type ChatbotConfigUpdate,
  type ChatbotWithIdentity,
} from '../types/chatbot';
import {
  hasChatbotIdentity,
  isAccessibleChatbotWithIdentity,
} from './chatbotServiceGuards';

export async function listChatbotsForKnowledgeBase(
  chatbotApi: ChatbotApi,
  knowledgeBaseId: string,
  limit = 1000,
): Promise<ChatbotWithIdentity[]> {
  const chatbots =
    await chatbotApi.deLiksChatbotBoundaryChatbotResourceListChatbots({
      knowledgeBaseId,
      limit,
    });
  return chatbots.filter(hasChatbotIdentity);
}

export async function listAccessibleChatbots(
  chatbotApi: ChatbotApi,
): Promise<AccessibleChatbotWithIdentity[]> {
  const pageSize = 1000;
  const accessibleChatbots: AccessibleChatbotWithIdentity[] = [];
  let offset = 0;

  for (;;) {
    const page =
      await chatbotApi.deLiksChatbotBoundaryChatbotConfigResourceListAccessibleChatbots(
        { limit: pageSize, offset },
      );

    accessibleChatbots.push(...page.filter(isAccessibleChatbotWithIdentity));

    if (page.length < pageSize) {
      break;
    }

    offset += pageSize;
  }

  return accessibleChatbots;
}

export async function getChatbot(
  chatbotApi: ChatbotApi,
  chatbotId: string,
): Promise<ChatbotWithIdentity> {
  const chatbot =
    await chatbotApi.deLiksChatbotBoundaryChatbotConfigResourceReadChatbot({
      chatbotId,
    });

  if (!hasChatbotIdentity(chatbot)) {
    throw new Error('Backend returned a chatbot without id and name');
  }

  return chatbot;
}

export async function createChatbot(
  chatbotApi: ChatbotApi,
  input: {
    knowledgeBaseId: string;
    name: string;
  },
): Promise<ChatbotWithIdentity> {
  const created =
    await chatbotApi.deLiksChatbotBoundaryChatbotResourceCreateChatbot({
      knowledgeBaseId: input.knowledgeBaseId,
      chatbot: {
        name: input.name.trim(),
        knowledgeBaseId: input.knowledgeBaseId,
        primaryColor: DEFAULT_CHATBOT_PRIMARY_COLOR,
      },
    });

  if (!hasChatbotIdentity(created)) {
    throw new Error('Backend returned a chatbot without id and name');
  }

  return created;
}

export async function updateChatbot(
  chatbotApi: ChatbotApi,
  chatbot: ChatbotWithIdentity,
  updates: ChatbotConfigUpdate,
): Promise<ChatbotWithIdentity> {
  if (
    typeof chatbot.id !== 'string' ||
    chatbot.id.length === 0 ||
    typeof chatbot.name !== 'string' ||
    chatbot.name.trim() === '' ||
    typeof chatbot.knowledgeBaseId !== 'string' ||
    chatbot.knowledgeBaseId.trim() === ''
  ) {
    throw new Error(
      'Cannot update chatbot without id, name, and knowledgeBaseId',
    );
  }

  const nextName =
    updates.name !== undefined ? (updates.name ?? '').trim() : chatbot.name;
  if (nextName === '') {
    throw new Error('Chatbot name must not be empty');
  }
  const nextDisplayName =
    updates.displayName !== undefined
      ? updates.displayName?.trim() || null
      : (chatbot.displayName ?? undefined);

  const nextWelcomeMessage =
    updates.welcomeMessage !== undefined
      ? updates.welcomeMessage?.trim() || null
      : (chatbot.welcomeMessage ?? undefined);
  const nextSystemPrompt =
    updates.systemPrompt !== undefined
      ? updates.systemPrompt?.trim() || null
      : (chatbot.systemPrompt ?? undefined);
  const nextPrimaryColor =
    updates.primaryColor !== undefined
      ? updates.primaryColor?.trim() || null
      : (chatbot.primaryColor ?? undefined);
  const nextCorsOrigins =
    updates.corsAllowedOrigins !== undefined
      ? (updates.corsAllowedOrigins as unknown as object)
      : chatbot.corsAllowedOrigins;
  const nextMaxResults =
    updates.maxResults !== undefined
      ? updates.maxResults
      : (chatbot.maxResults ?? undefined);
  const nextMinScore =
    updates.minScore !== undefined
      ? updates.minScore
      : (chatbot.minScore ?? undefined);

  const updated =
    await chatbotApi.deLiksChatbotBoundaryChatbotConfigResourceUpdateChatbot({
      chatbotId: chatbot.id,
      chatbot: {
        id: chatbot.id,
        name: nextName,
        knowledgeBaseId: chatbot.knowledgeBaseId,
        displayName: nextDisplayName,
        tenantId: chatbot.tenantId ?? undefined,
        isPublic:
          updates.isPublic !== undefined
            ? updates.isPublic
            : (chatbot.isPublic ?? undefined),
        welcomeMessage: nextWelcomeMessage,
        systemPrompt: nextSystemPrompt,
        primaryColor: nextPrimaryColor,
        corsAllowCredentials:
          updates.corsAllowCredentials !== undefined
            ? updates.corsAllowCredentials
            : (chatbot.corsAllowCredentials ?? undefined),
        corsAllowedOrigins: nextCorsOrigins,
        placement:
          updates.placement !== undefined
            ? (updates.placement as Chatbot['placement'])
            : chatbot.placement,
        maxResults: nextMaxResults,
        minScore: nextMinScore,
      },
    });

  if (!hasChatbotIdentity(updated)) {
    throw new Error('Backend returned a chatbot without id and name');
  }

  return updated;
}

export async function downloadChatbotLogo(
  chatbotApi: ChatbotApi,
  chatbotId: string,
): Promise<Blob | null | undefined> {
  return chatbotApi.deLiksChatbotBoundaryChatbotConfigResourceDownloadChatbotLogo(
    { chatbotId },
  );
}

export async function uploadChatbotLogo(
  chatbotApi: ChatbotApi,
  chatbotId: string,
  file: File,
): Promise<ChatbotWithIdentity> {
  const updated =
    await chatbotApi.deLiksChatbotBoundaryChatbotConfigResourceUploadChatbotLogo(
      {
        chatbotId,
        data: file,
        fileName: file.name,
        contentType: file.type || 'application/octet-stream',
        size: file.size,
      },
    );

  if (!hasChatbotIdentity(updated)) {
    throw new Error('Backend returned a chatbot without id and name');
  }

  return updated;
}

export async function deleteChatbotLogo(
  chatbotApi: ChatbotApi,
  chatbotId: string,
): Promise<void> {
  await chatbotApi.deLiksChatbotBoundaryChatbotConfigResourceDeleteChatbotLogo({
    chatbotId,
  });
}

export async function deleteChatbot(
  chatbotApi: ChatbotApi,
  chatbotId: string,
): Promise<void> {
  await chatbotApi.deLiksChatbotBoundaryChatbotConfigResourceDeleteChatbot({
    chatbotId,
  });
}
